/*
 * "A Little Secret" campaign (owner 2026-10-23, /pages/little-secret).
 *
 * <little-secret-offer> (sections/little-secret-hero.liquid) asks the Gobi
 * Loyalty app proxy (/apps/loyalty/secret) for the visitor's state and shows
 * one of the server-rendered panels. A visitor counts as invited when the page
 * was opened from the campaign email (utm_medium=email + utm_campaign = the
 * section's value); that is remembered in localStorage until the campaign ends.
 * Invited + logged in + returning customer → a code is reserved (POST) and
 * shown with its real 72-hour countdown. Invited + logged out → login dialog.
 *
 * <little-secret-cart> (snippets/little-secret-cart.liquid, inside the
 * re-rendered cart body) keeps the reserved code on the cart while an edit item
 * is in it, and takes it off when the last edit item leaves.
 */
(() => {
  if (customElements.get('little-secret-offer')) return;

  const RESERVATION_KEY = 'gobi:little-secret';
  const INVITE_KEY = 'gobi:little-secret:invited';
  const DIALOG_KEY = 'gobi:little-secret:dialog';
  const CHECKED_KEY = 'gobi:little-secret:checked';
  const LAST_SYNC_KEY = 'gobi:little-secret:last';
  const CODE_PREFIX = 'SECRET-';

  const isSecret = (code) => typeof code === 'string' && code.toUpperCase().startsWith(CODE_PREFIX);

  // Storage can be blocked (private mode, cookie settings); everything still works
  // for the current page without it.
  const store = (kind) => {
    try {
      const s = window[kind];
      s.setItem('__ls', '1');
      s.removeItem('__ls');
      return s;
    } catch (error) {
      return null;
    }
  };
  const local = store('localStorage');
  const session = store('sessionStorage');
  const read = (s, key) => {
    if (!s) return null;
    try {
      return JSON.parse(s.getItem(key) || 'null');
    } catch (error) {
      return null;
    }
  };
  const write = (s, key, value) => {
    if (!s) return;
    try {
      if (value == null) s.removeItem(key);
      else s.setItem(key, JSON.stringify(value));
    } catch (error) {
      // ignore
    }
  };

  const reservation = () => {
    const r = read(local, RESERVATION_KEY);
    if (r && isSecret(r.code) && Date.parse(r.expiresAt) > Date.now()) return r;
    if (r) write(local, RESERVATION_KEY, null);
    return null;
  };

  // Remember a reserved code; forget it once the server says it is gone.
  const remember = (state) => {
    if (!state) return;
    if (state.status === 'reserved' && isSecret(state.code)) {
      write(local, RESERVATION_KEY, { code: state.code, expiresAt: state.expiresAt });
    } else if (['used', 'expired', 'ended', 'ineligible', 'off'].includes(state.status)) {
      write(local, RESERVATION_KEY, null);
    }
  };

  const fetchState = async (endpoint, method = 'GET') => {
    const response = await fetch(endpoint, {
      method,
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    const data = await response.json().catch(() => ({}));
    if (!data || !data.status) throw new Error(`little-secret state ${response.status}`);
    return data;
  };

  // ---- cart ----

  let syncing = false;
  let syncAgain = false;

  const refreshCart = async () => {
    if (document.getElementById('main-cart-items')) {
      window.location.reload();
      return;
    }
    const drawer = document.querySelector('cart-drawer');
    try {
      const sections = drawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map((section) => section.section)
        : ['cart-drawer', 'cart-counter'];
      const rendered = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`).then((r) => r.json());
      if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents({ sections: rendered }, false);
      if (typeof updateFreeShipping === 'function') updateFreeShipping();
    } catch (error) {
      // the next cart change re-renders anyway
    }
  };

  const syncCart = async (marker) => {
    if (!marker || !window.routes) return;
    if (syncing) {
      syncAgain = true;
      return;
    }
    syncing = true;
    try {
      const editItems = Number(marker.dataset.editItems || 0);
      let held = reservation();

      // Reserved on another device: ask once per session, only when it matters.
      if (!held && editItems > 0 && marker.dataset.loggedIn === 'true' && !read(session, CHECKED_KEY)) {
        write(session, CHECKED_KEY, 1);
        try {
          remember(await fetchState(marker.dataset.endpoint));
          held = reservation();
        } catch (error) {
          // no code this time
        }
      }

      const cart = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const codes = (cart.discount_codes || []).map((d) => d.code).filter(Boolean);
      const others = codes.filter((code) => !isSecret(code));
      const want = held && editItems > 0 ? held.code : null;
      const next = want ? others.concat(want) : others;
      const upper = (list) => list.map((c) => c.toUpperCase()).sort().join(',');
      if (upper(next) === upper(codes)) return;

      // Loop guard: never send the same change twice within a few seconds.
      const signature = next.join(',');
      const last = read(session, LAST_SYNC_KEY);
      if (last && last.signature === signature && Date.now() - last.at < 15000) return;
      write(session, LAST_SYNC_KEY, { signature, at: Date.now() });

      // `discount` adds to the cart's codes and '' clears them all, so taking one
      // code off means clearing, then putting the others back.
      const update = (discount) => fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({ discount })
      });
      const removing = codes.some((c) => !next.some((n) => n.toUpperCase() === c.toUpperCase()));
      if (removing) await update('');
      if (next.length) await update(signature);
      await refreshCart();
    } catch (error) {
      // best effort; the code can still be typed at checkout
    } finally {
      syncing = false;
      if (syncAgain) {
        syncAgain = false;
        syncCart(document.querySelector('little-secret-cart'));
      }
    }
  };

  class LittleSecretCart extends HTMLElement {
    connectedCallback() {
      window.setTimeout(() => syncCart(this), 0);
    }
  }

  // ---- landing page ----

  const pad = (n) => String(n).padStart(2, '0');

  class LittleSecretOffer extends HTMLElement {
    connectedCallback() {
      this.endpoint = this.dataset.endpoint;
      this.invited = this.checkInvite();
      this.claimed = false;
      this.dialog = this.querySelector('[data-ls-dialog]');

      const copy = this.querySelector('[data-ls-copy]');
      if (copy) copy.addEventListener('click', () => this.copy());
      const claim = this.querySelector('[data-ls-claim]');
      if (claim) claim.addEventListener('click', () => this.claim());
      this.querySelectorAll('[data-ls-dialog-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeDialog());
      });
      if (this.dialog) this.dialog.addEventListener('cancel', () => write(session, DIALOG_KEY, 1));

      this.load();
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    checkInvite() {
      const params = new URLSearchParams(window.location.search);
      const campaign = (this.dataset.campaign || '').trim().toLowerCase();
      const endsAt = Date.parse(this.dataset.endsAt) || Date.now() + 10 * 86400000;
      if (
        campaign &&
        (params.get('utm_medium') || '').toLowerCase() === 'email' &&
        (params.get('utm_campaign') || '').toLowerCase() === campaign
      ) {
        write(local, INVITE_KEY, { until: endsAt });
        return true;
      }
      const invite = read(local, INVITE_KEY);
      return Boolean(invite && invite.until > Date.now());
    }

    async load() {
      try {
        this.render(await fetchState(this.endpoint));
      } catch (error) {
        this.show('error');
      }
    }

    render(state) {
      remember(state);
      this.updateCount(state);
      switch (state.status) {
        case 'reserved':
          this.showCode(state);
          break;
        case 'available':
          if (this.invited && !this.claimed) {
            this.claim();
          } else {
            this.show('invite');
          }
          break;
        case 'anonymous':
          if (this.invited) {
            this.show('login');
            this.openDialog();
          } else {
            this.show('invite');
          }
          break;
        case 'expired':
        case 'used':
        case 'ineligible':
        case 'allreserved':
        case 'soldout':
        case 'ended':
          this.show(state.status);
          break;
        case 'off':
          this.show('invite');
          break;
        default:
          this.show('error');
      }
    }

    async claim() {
      this.claimed = true;
      const button = this.querySelector('[data-ls-claim]');
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }
      try {
        this.render(await fetchState(this.endpoint, 'POST'));
      } catch (error) {
        this.show('error');
      } finally {
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }
    }

    showCode(state) {
      this.code = state.code;
      const value = this.querySelector('[data-ls-code]');
      if (value) value.textContent = state.code;
      this.expiresAt = Date.parse(state.expiresAt);
      this.show('reserved');
      this.tick();
      window.clearInterval(this.timer);
      this.timer = window.setInterval(() => this.tick(), 1000);
      document.querySelectorAll('little-secret-cart').forEach((marker) => syncCart(marker));
    }

    tick() {
      const left = Math.max(0, Math.floor((this.expiresAt - Date.now()) / 1000));
      const set = (selector, text) => {
        const el = this.querySelector(selector);
        if (el && el.textContent !== text) el.textContent = text;
      };
      set('[data-ls-hh]', pad(Math.floor(left / 3600)));
      set('[data-ls-mm]', pad(Math.floor((left % 3600) / 60)));
      set('[data-ls-ss]', pad(left % 60));
      if (left === 0) {
        window.clearInterval(this.timer);
        write(local, RESERVATION_KEY, null);
        this.load();
      }
    }

    show(name) {
      this.querySelectorAll('[data-ls-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.lsPanel !== name;
      });
    }

    updateCount(state) {
      const box = this.querySelector('[data-ls-count]');
      if (!box) return;
      const total = Number(state.total) || 0;
      const left = Math.max(0, Number(state.left) || 0);
      const visible = total > 0 && !['ended', 'off', 'error'].includes(state.status);
      box.hidden = !visible;
      if (!visible) return;
      const leftEl = box.querySelector('[data-ls-left]');
      const totalEl = box.querySelector('[data-ls-total]');
      const bar = box.querySelector('[data-ls-bar]');
      if (leftEl) leftEl.textContent = String(left);
      if (totalEl) totalEl.textContent = String(total);
      if (bar) bar.style.width = `${Math.min(100, (left / total) * 100).toFixed(1)}%`;
    }

    openDialog() {
      if (!this.dialog || read(session, DIALOG_KEY) || typeof this.dialog.showModal !== 'function') return;
      this.dialog.showModal();
    }

    closeDialog() {
      write(session, DIALOG_KEY, 1);
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    copy() {
      if (!this.code) return;
      const label = this.querySelector('[data-ls-copy-label]');
      const done = () => {
        if (!label) return;
        label.textContent = this.dataset.copied || 'Copied';
        window.clearTimeout(this.copyTimer);
        this.copyTimer = window.setTimeout(() => {
          label.textContent = this.dataset.copy || 'Copy';
        }, 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.code).then(done, () => {});
      }
    }
  }

  customElements.define('little-secret-cart', LittleSecretCart);
  customElements.define('little-secret-offer', LittleSecretOffer);
})();
